import type { DepartureTiming, FareComponentSummary, TaxesByTicket } from './types'

// Bare "CHARGE {CCY} {amount}." under a CANCELLATIONS header, where the header is the qualifier
// rather than an inline "FOR ..." clause (QR: "CANCELLATIONS\nANY TIME\nCHARGE USD 250.00.").
// The header sometimes splits into BEFORE/AFTER DEPARTURE tiers instead (ET). Bounded to the
// CANCELLATIONS section so a sibling CHANGES section in the same shape is never picked up.
const CANCELLATIONS_HEADER_RE = /^CANCELLATIONS\s*$/im
const CANCELLATIONS_SECTION_END_RE = /^(?:CHANGES\s*|NOTE\s*[-–].*)$/im
const FLAT_BARE_CHARGE_RE = /^(?:ANY\s*TIME\s*\n)?CHARGE\s+([A-Z]{3})\s+(\d+(?:\.\d+)?)\.\s*$/im
const TIERED_BARE_CHARGE_LINE_RE = /^CHARGE\s+([A-Z]{3})\s+(\d+(?:\.\d+)?)\.\s*$/im
const BEFORE_DEPARTURE_HEADER_RE = /^BEFORE\s+DEPARTURE\s*$/im
const AFTER_DEPARTURE_HEADER_RE = /^AFTER\s+DEPARTURE\s*$/im

function cancellationsBareCharges(text: string, timing?: DepartureTiming): Array<{ currency: string; amount: number }> {
  const headerMatch = CANCELLATIONS_HEADER_RE.exec(text)
  if (!headerMatch) return []

  const afterHeader = text.slice(headerMatch.index + headerMatch[0].length)
  const endMatch = CANCELLATIONS_SECTION_END_RE.exec(afterHeader)
  const section = endMatch ? afterHeader.slice(0, endMatch.index) : afterHeader

  const beforeIdx = BEFORE_DEPARTURE_HEADER_RE.exec(section)?.index ?? -1
  const afterIdx = AFTER_DEPARTURE_HEADER_RE.exec(section)?.index ?? -1

  if (beforeIdx < 0 && afterIdx < 0) {
    const flat = FLAT_BARE_CHARGE_RE.exec(section)
    return flat ? [{ currency: flat[1].toUpperCase(), amount: parseFloat(flat[2]) }] : []
  }

  const results: Array<{ currency: string; amount: number }> = []

  if (timing !== 'AFTER_DEPARTURE' && beforeIdx >= 0) {
    const tierEnd = afterIdx > beforeIdx ? afterIdx : section.length
    const m = TIERED_BARE_CHARGE_LINE_RE.exec(section.slice(beforeIdx, tierEnd))
    if (m) results.push({ currency: m[1].toUpperCase(), amount: parseFloat(m[2]) })
  }

  if (timing !== 'BEFORE_DEPARTURE' && afterIdx >= 0) {
    const m = TIERED_BARE_CHARGE_LINE_RE.exec(section.slice(afterIdx))
    if (m) results.push({ currency: m[1].toUpperCase(), amount: parseFloat(m[2]) })
  }

  return results
}

// Returns the cancellation penalty from Cat 16 text, or null.
// Matches: "CHARGE {CCY} {amount} FOR [CANCEL/]REFUND."
// When the penalty currency differs from bookingCurrency, fareToBookingCurrencyRate is applied to convert.
// Pass timing to scope the search to only the relevant BEFORE/AFTER DEPARTURE section.
//
// Strategy: max within each fare component, then max across components — carriers like EK file
// "COLLECT HIGHEST" per pricing unit, and taking the min would under-penalise and over-refund.
// Before departure, a fee filed "PER ONE WAY" / "PER FARE COMPONENT" on every component is added
// up across the journey instead: an SYD-ICN return at AUD 220 per one way costs 440 (PLT2956358).
export function parseCat16CancellationPenalty(
  fareRules: FareComponentSummary[],
  bookingCurrency?: string,
  fareToBookingCurrencyRate?: number,
  timing?: DepartureTiming,
): number | null {
  const penalty = parseScopedCancellationPenalty(fareRules, bookingCurrency, fareToBookingCurrencyRate, timing, false)
  if (penalty !== null || timing !== 'BEFORE_DEPARTURE') return penalty

  // The cut above stops at the first "AFTER DEPARTURE" anywhere, including outside CANCELLATIONS: a
  // CHANGES tier (KQ, B6), a reissue condition (VA), a name-change line (TG, PLT2964240: "CORRECT
  // NAME AFTER DEPARTURE"). The fee below it is lost, and with it the Cat 16 cap on Sabre's figure.
  // When the cut finds nothing, read only the cancellation sections, minus the blocks under a
  // standalone AFTER DEPARTURE heading. Fallback only: applied always, it moved 8 correct figures
  // across 1,363 quoted bookings (OZ, SV).
  return parseScopedCancellationPenalty(fareRules, bookingCurrency, fareToBookingCurrencyRate, timing, true)
}

// "COLLECT THE HIGHEST" across components overrides any per-one-way wording (EK and others).
const COLLECT_HIGHEST_RE = /\bHIGHEST\b/i
const CANCELLATION_SECTION_HEADING_RE = /^(?:(?:CHANGES\/)?CANCELLATIONS|[A-Z]\.\s+REFUND\s+FEE)$/i
const CHANGES_HEADING_RE = /^CHANGES$/i
const AFTER_DEPARTURE_HEADING_RE = /^AFTER\s+DEPARTURE$/i
const TIER_END_HEADING_RE = /^(?:BEFORE\s+DEPARTURE|ANY\s*TIME|CHANGES)$/i

// A CHANGES section can file its own "FOR REFUND" charge, so with section headings present only the
// CANCELLATIONS / REFUND FEE sections are kept. Without any, the whole text is.
function beforeDepartureCancellationText(text: string): string {
  const lines = text.split('\n')
  const hasSections = lines.some(line => CANCELLATION_SECTION_HEADING_RE.test(line.trim()))
  const kept: string[] = []
  let inSection = !hasSections
  let skipping = false

  for (const line of lines) {
    const heading = line.trim()
    if (CANCELLATION_SECTION_HEADING_RE.test(heading)) {
      inSection = true
      skipping = false
    } else if (hasSections && CHANGES_HEADING_RE.test(heading)) {
      inSection = false
    }

    if (!inSection) continue

    if (AFTER_DEPARTURE_HEADING_RE.test(heading)) {
      skipping = true
      continue
    }

    if (skipping && TIER_END_HEADING_RE.test(heading)) skipping = false
    if (!skipping) kept.push(line)
  }

  return kept.join('\n')
}

function parseScopedCancellationPenalty(
  fareRules: FareComponentSummary[],
  bookingCurrency: string | undefined,
  fareToBookingCurrencyRate: number | undefined,
  timing: DepartureTiming | undefined,
  headingsOnly: boolean,
): number | null {
  let overallMax: number | null = null
  // The highest fee per origin-destination, and whether every fee found was filed per one way.
  // Fare rules repeat each component once per passenger, so keying on originDestination keeps the
  // sum to one passenger's journey.
  const maxByOriginDestination = new Map<string, number>()
  let everyFeePerOneWay = true
  let collectsHighest = false

  for (const component of fareRules) {
    // A rule often files the SAME penalty in both the booking currency and a foreign one ("CHARGE
    // CNY 1500 ... CHARGE AUD 300 FOR CANCEL/REFUND"). The booking-currency figure is exact; the
    // foreign one is that same fee approximated through an FX rate — prefer native when present so a
    // rounded conversion (AUD 316) never beats the filed AUD 300 (HIINNW / MU). Only when no native
    // amount is filed do we fall back to the converted foreign one.
    let componentNativeMax: number | null = null
    let componentConvertedMax: number | null = null
    let componentPerOneWay = true

    const applyAmount = (penaltyCurrency: string, rawAmount: number) => {
      if (!bookingCurrency || penaltyCurrency === bookingCurrency.toUpperCase()) {
        if (componentNativeMax === null || rawAmount > componentNativeMax) componentNativeMax = rawAmount
      } else if (fareToBookingCurrencyRate != null && fareToBookingCurrencyRate > 0) {
        const amount = Math.round(rawAmount * fareToBookingCurrencyRate)
        if (componentConvertedMax === null || amount > componentConvertedMax) componentConvertedMax = amount
      }
    }

    for (const rule of component.rules) {
      if (!isCat16Rule(rule)) continue
      if (COLLECT_HIGHEST_RE.test(rule.text)) collectsHighest = true

      // Scope text to the timing-relevant section when timing is known.
      // "AFTER DEPARTURE" is a section header — everything before it is the before-departure block.
      let text = rule.text
      if (timing === 'BEFORE_DEPARTURE' && headingsOnly) {
        text = beforeDepartureCancellationText(text)
      } else if (timing === 'BEFORE_DEPARTURE') {
        const afterIdx = text.search(/\bAFTER\s+DEPARTURE\b/i)
        if (afterIdx >= 0) text = text.slice(0, afterIdx)
      } else if (timing === 'AFTER_DEPARTURE') {
        const afterIdx = text.search(/\bAFTER\s+DEPARTURE\b/i)
        if (afterIdx >= 0) text = text.slice(afterIdx)
      }

      // Format 1: "CHARGE AUD 125.00 FOR [CANCEL/]REFUND" or "FOR CANCELLATION"
      const chargeRe =
        /(PER\s+(?:ONE\s+WAY|FARE\s+COMPONENT|DIRECTION)\s+)?CHARGE\s+([A-Z]{3})\s+(\d+(?:\.\d+)?)\s+FOR\s+(?:CANCEL(?:LATION|\/REFUND)?|REFUND)/gi
      let match: RegExpExecArray | null
      while ((match = chargeRe.exec(text)) !== null) {
        if (!match[1]) componentPerOneWay = false
        applyAmount(match[2].toUpperCase(), parseFloat(match[3]))
      }

      // Format 2: tabular fee table with BEFORE/AFTER FLIGHT lines inside a REFUND FEE section.
      // e.g.: "B. REFUND FEE\n- BEFORE FLIGHT AUD 125.00\n- AFTER FLIGHT AUD 225.00"
      const refundFeeIndex = text.search(/^[A-Z]\.\s+REFUND\s+FEE\s*$/im)
      if (refundFeeIndex >= 0) {
        const flightFeeRe = /[-–]\s*(BEFORE|AFTER)\s+FLIGHT\s+([A-Z]{3})\s+(\d+(?:\.\d+)?)/gi
        let m2: RegExpExecArray | null
        while ((m2 = flightFeeRe.exec(text.slice(refundFeeIndex))) !== null) {
          if (timing === 'BEFORE_DEPARTURE' && m2[1].toUpperCase() === 'AFTER') continue
          if (timing === 'AFTER_DEPARTURE' && m2[1].toUpperCase() === 'BEFORE') continue
          componentPerOneWay = false
          applyAmount(m2[2].toUpperCase(), parseFloat(m2[3]))
        }
      }

      // Format 3: bare "CHARGE {CCY} {amount}." under a CANCELLATIONS header (see
      // cancellationsBareCharges). Uses the original rule.text, not the timing-sliced `text`
      // above — a shared CANCELLATIONS header spanning both BEFORE/AFTER tiers would otherwise
      // get cut off by that slice before this function ever sees it.
      for (const { currency, amount } of cancellationsBareCharges(rule.text, timing)) {
        componentPerOneWay = false
        applyAmount(currency, amount)
      }
    }

    const componentMax = componentNativeMax ?? componentConvertedMax
    if (componentMax !== null) {
      if (overallMax === null || componentMax > overallMax) overallMax = componentMax
      if (!componentPerOneWay) everyFeePerOneWay = false
      const seen = maxByOriginDestination.get(component.originDestination)
      if (seen === undefined || componentMax > seen)
        maxByOriginDestination.set(component.originDestination, componentMax)
    }
  }

  // After departure part of the journey is flown and its fee no longer applies, so only the
  // whole-journey case adds up.
  if (overallMax !== null && timing === 'BEFORE_DEPARTURE' && everyFeePerOneWay && !collectsHighest) {
    return [...maxByOriginDestination.values()].reduce((sum, amount) => sum + amount, 0)
  }

  return overallMax
}

// Warns when GDS penalty exceeds Cat 16 text — Sabre may be using the higher OR-block, not the timing-applicable tier.
export function buildPenaltyMismatchWarning(
  ticketPenaltyMap: Record<string, number>,
  cat16CancellationPenalty: number | null | undefined,
): string | null {
  if (cat16CancellationPenalty == null) return null

  const entries = Object.values(ticketPenaltyMap)
  if (entries.length === 0) return null

  const maxGdsPenalty = Math.max(...entries)
  if (maxGdsPenalty <= cat16CancellationPenalty) return null

  return (
    `GDS penalty (USD ${maxGdsPenalty}) exceeds Cat 16 text (USD ${cat16CancellationPenalty}) — ` +
    `Sabre may be applying the higher penalty tier. ` +
    `If cancelling ≥24h before departure, override to USD ${cat16CancellationPenalty} per ticket.`
  )
}

// Finds non-refundable tax codes in the Cat 16 VOLUNTARY REFUND section (e.g. "YR IS NON-REFUNDABLE").
export function parseCat16NonRefundableTaxCodes(fareRules: FareComponentSummary[]): Set<string> {
  const codes = new Set<string>()

  for (const component of fareRules) {
    for (const rule of component.rules) {
      if (!isCat16Rule(rule)) continue

      // Extract the VOLUNTARY REFUND section (up to INVOLUNTARY REFUND or CHANGES)
      const voluntaryMatch = rule.text.match(
        /VOLUNTARY\s+REFUND[^\n]*\n([\s\S]*?)(?=INVOLUNTARY\s+REFUND|^CHANGES\s*$|$)/im,
      )
      if (!voluntaryMatch) continue

      const section = voluntaryMatch[1]

      // Match patterns: "YR IS NON-REFUNDABLE" or "YR AND YQ ARE NON-REFUNDABLE"
      const nonRefundableRe = /\b([A-Z]{2})\b(?:\s+AND\s+\b([A-Z]{2})\b)?\s+(?:IS|ARE)\s+NON-REFUNDABLE/gi
      let m: RegExpExecArray | null
      while ((m = nonRefundableRe.exec(section)) !== null) {
        codes.add(m[1].toUpperCase())
        if (m[2]) codes.add(m[2].toUpperCase())
      }
    }
  }

  return codes
}

// Carrier surcharges (YQ/YR) the fare declares non-refundable ONLY when the ticket itself is
// non-refundable — e.g. "FOR NON REFUNDABLE TICKETS - THE YQ/YR WILL NOT BE REFUNDED" (MU / China
// Eastern, HIINNW). On a REFUNDABLE ticket these follow the fare and come back, so the caller must
// drop them from the YQ/YR withholding floor rather than treating them as unconditionally withheld.
export function parseCat16ConditionalNonRefundableTaxCodes(fareRules: FareComponentSummary[]): Set<string> {
  const codes = new Set<string>()
  // "FOR NON[-]REFUNDABLE TICKET(S) ... YQ[/YR] ... WILL NOT BE REFUNDED", tolerant of line breaks
  // and the "- THE " filler between the qualifier and the codes. Bounded gaps avoid over-matching.
  const re =
    /FOR\s+NON[\s-]*REFUNDABLE\s+TICKETS?\b[\s\S]{0,40}?\b(YQ|YR)\b(?:\s*\/\s*(YQ|YR)\b)?[\s\S]{0,20}?WILL\s+NOT\s+BE\s+REFUNDED/gi

  for (const component of fareRules) {
    for (const rule of component.rules) {
      if (!isCat16Rule(rule)) continue
      re.lastIndex = 0
      let m: RegExpExecArray | null
      while ((m = re.exec(rule.text)) !== null) {
        codes.add(m[1].toUpperCase())
        if (m[2]) codes.add(m[2].toUpperCase())
      }
    }
  }

  return codes
}

export function computeCat16NonRefundableTaxes(
  fareRules: FareComponentSummary[],
  allTaxesByTicket: TaxesByTicket | undefined,
  selectedTicketNumbers: Set<string>,
  bookingCurrency: string,
): number | null {
  return sumNonRefundableTaxes(
    parseCat16NonRefundableTaxCodes(fareRules),
    allTaxesByTicket,
    selectedTicketNumbers,
    bookingCurrency,
  )
}

/** Sum the selected tickets' taxes whose codes the fare rules declare non-refundable. */
export function sumNonRefundableTaxes(
  nonRefundableCodes: Set<string>,
  allTaxesByTicket: TaxesByTicket | undefined,
  selectedTicketNumbers: Set<string>,
  bookingCurrency: string,
): number | null {
  if (!allTaxesByTicket || nonRefundableCodes.size === 0) return null

  let total = 0
  for (const [ticketNumber, taxes] of Object.entries(allTaxesByTicket)) {
    if (!selectedTicketNumbers.has(ticketNumber)) continue
    for (const tax of taxes) {
      if (!nonRefundableCodes.has(tax.taxCode.toUpperCase())) continue
      if (tax.currencyCode !== bookingCurrency) continue
      total += parseFloat(tax.amount) || 0
    }
  }

  return total > 0 ? total : null
}

export interface Cat16PctTier {
  beforeDays: number
  pct: number
}

// Extracts tiered percentage penalties from Cat 16 text.
// Matches: "BEFORE {N} DAY(S) TO DEPARTURE - PENALTY APPLIES PCT {N} OF THE FARE"
// Returns tiers sorted descending by beforeDays (most lenient first).
export function parseCat16PctPenaltyTiers(fareRules: FareComponentSummary[]): Cat16PctTier[] {
  const seen = new Map<string, Cat16PctTier>()

  for (const component of fareRules) {
    for (const rule of component.rules) {
      if (!isCat16Rule(rule)) continue

      const re = /BEFORE\s+(\d+)\s+DAYS?\s+TO\s+DEPARTURE\s*[-–]\s*PENALTY\s+APPLIES\s+PCT\s+(\d+)\s+OF\s+THE\s+FARE/gi
      let match: RegExpExecArray | null
      while ((match = re.exec(rule.text)) !== null) {
        const beforeDays = parseInt(match[1])
        const pct = parseInt(match[2])
        seen.set(`${beforeDays}:${pct}`, { beforeDays, pct })
      }
    }
  }

  return [...seen.values()].sort((a, b) => b.beforeDays - a.beforeDays)
}

// Returns the applicable tier given days until departure, or null if none apply
// (i.e. within the last day / "NOT PERMITTED" window or after departure).
export function resolveCat16PctTier(tiers: Cat16PctTier[], daysUntilDeparture: number): Cat16PctTier | null {
  return tiers.find(t => daysUntilDeparture >= t.beforeDays) ?? null
}

// True when Cat 16 declares the ticket unconditionally non-refundable. Matches "TICKET IS
// NON-REFUNDABLE" / "NO REFUND PERMITTED", but only when no rule in the same fare component
// carries a cancel/refund CHARGE — alongside a charge the phrase is section-scoped (e.g. AFTER
// DEPARTURE only). Per-component so a split pair (rule A: CHARGE, rule B: NON-REFUNDABLE) reads
// as one fare.
export function parseCat16IsTicketNonRefundable(fareRules: FareComponentSummary[]): boolean {
  const chargeRe = /CHARGE\s+[A-Z]{3}\s+\d+(?:\.\d+)?\s+FOR\s+(?:CANCEL(?:LATION|\/REFUND)|REFUND)/i

  for (const component of fareRules) {
    const componentHasCharge = component.rules.some(
      r => isCat16Rule(r) && (chargeRe.test(r.text) || cancellationsBareCharges(r.text).length > 0),
    )
    if (componentHasCharge) continue

    for (const rule of component.rules) {
      if (isCat16Rule(rule) && hasUnconditionalNonRefundable(rule.text)) return true
    }
  }
  return false
}

const NON_REFUNDABLE_PHRASE = /TICKET\s+IS\s+NON-REFUNDABLE|NO\s+REFUND\s+PERMITTED/gi
const CONDITIONAL_CLAUSE = /\bIF\b|AFTER\s+DEPARTURE|NO-?\s?SHOW|FAILS\s+TO|RESIDUAL|NEW\s+FARE/i
const NO_SHOW_ONLY = /^\s*(?:IN\s+CASE\s+OF|FOR)\s+NO-?\s?SHOW\b(?!\s*\/)/i

// A phrase scoped to a no-show, a failure to cancel, after departure, or the residual of a change
// leaves an ordinary cancellation refundable: "NO REFUND PERMITTED IN CASE OF NO SHOW" (AC), "IF
// NEW FARE IS LOWER ... NO REFUND PERMITTED" (TS), "AFTER DEPARTURE THE TICKET IS NON-REFUNDABLE" (AA).
function hasUnconditionalNonRefundable(text: string): boolean {
  for (const match of text.matchAll(NON_REFUNDABLE_PHRASE)) {
    const before = text.slice(0, match.index)
    const clause = before.slice(Math.max(before.lastIndexOf('.'), before.lastIndexOf('---')) + 1)
    const after = text.slice(match.index + match[0].length)
    if (!CONDITIONAL_CLAUSE.test(clause) && !NO_SHOW_ONLY.test(after)) return true
  }
  return false
}

function isCat16Rule(rule: { category?: string; title: string }): boolean {
  return rule.category === '16' || /penalt/i.test(rule.title)
}
